import React, { useState } from 'react';
import { Gift, IndianRupee, AlertTriangle, Info } from 'lucide-react';
import Modal from './Modal';
import Input from './elements/Input';
import Button from './elements/Button';
import { requestReferralClaim } from '../utils/authApi';

const ClaimReferralModal = ({ 
    open, 
    onClose, 
    selectedReferral, 
    savedUpiId, 
    onClaimSuccess 
}) => {
    const [upiId, setUpiId] = useState(savedUpiId || '');
    const [claimLoading, setClaimLoading] = useState(false);
    const [claimError, setClaimError] = useState('');

    React.useEffect(() => {
        if (open) {
            setUpiId(savedUpiId || '');
            setClaimError('');
        }
    }, [open, savedUpiId]);

    const handleSubmitClaim = async (e) => {
        e.preventDefault();
        if (!upiId.trim()) {
            setClaimError('Please enter a valid UPI ID');
            return;
        }

        setClaimLoading(true);
        setClaimError('');

        try {
            const result = await requestReferralClaim(selectedReferral.id, upiId);
            if (result.success) {
                onClose();
                if (onClaimSuccess) {
                    onClaimSuccess();
                }
            } else {
                setClaimError(result.message || 'Failed to submit claim');
            }
        } catch (err) {
            setClaimError(err.message || 'Failed to submit claim');
        } finally {
            setClaimLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            icon={<Gift />}
            title="Claim Referral Reward"
            className="max-w-md"
        >
            <div className="w-full">
                {selectedReferral && (
                    <div className="border border-green-500 rounded-lg p-4 mb-4 bg-background-light text-primary-dark relative">
                        <span className="absolute -top-3 left-4 bg-green-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow">
                            Reward Details
                        </span>
                        <div className="text-sm space-y-1">
                            <div>
                                <span className="font-bold">Referral:</span> {`${selectedReferral.firstName || ''} ${selectedReferral.lastName || ''}`.trim() || selectedReferral.email}
                            </div>
                            <div className="flex items-center">
                                <span className="font-bold">Amount:</span> 
                                <span className="ml-1 text-green-700 font-semibold flex items-center gap-1">
                                    <IndianRupee size={16} />{selectedReferral.referralAmount}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmitClaim} className="flex flex-col gap-3 font-sans">
                    <Input
                        label="UPI ID"
                        type="text"
                        value={upiId}
                        onChange={(e) => setUpiId(e.target.value)}
                        placeholder="example@upi"
                        required
                        autoFocus
                    />

                    {claimError && (
                        <div className="p-2 text-red-700 bg-red-100 rounded-lg flex items-center mb-3">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            {claimError}
                        </div>
                    )}

                    <div className="border border-blue-500 bg-background-light text-primary-dark rounded-lg p-3 relative">
                        <span className="absolute -top-3 left-4 bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow flex items-center gap-1">
                            <Info className="w-3 h-3" /> Important Information
                        </span>
                        <div className="text-xs space-y-1">
                            <p>• Confirm your UPI ID to receive the referral reward</p>
                            <p>• Payment will be processed within 2-3 business days</p>
                            <p>• We store this UPI ID only until your payout is settled, then delete it</p>
                        </div>
                    </div>

                    <div className="flex gap-2 mt-2 justify-end">
                        <Button
                            type="secondary"
                            onClick={onClose}
                            disabled={claimLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={claimLoading}
                            disabled={claimLoading}
                        >
                            Submit Claim
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default ClaimReferralModal;

